import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query"
import { sdk } from "@/api/sdk"
import { queryClient } from "./client"
import {
  type AnalysisCompInput,
  type AnalysisCompType,
  type AnalysisQuery,
  type CreateAnalysisCompMutationMutation,
  type EditAnalysisMutationVariables,
  type Exact,
} from "@/generated/graphql"
import { type AnalyzerUserType } from "@/types/analysisTypes"
import { addComp, compsDataStore } from "@/stores/compsDataStore"

export const useAnalyses = (
  ordering: string,
  buyBoxId: string | null,
  onlyFavorites = false,
  onlySourced = false
) => {
  return useInfiniteQuery({
    queryKey: ["analyses", ordering, buyBoxId, onlyFavorites, onlySourced],
    queryFn: async ({ pageParam }) => {
      const requestPayload = {
        ordering,
        page: pageParam,
        buyBoxId,
        onlyFavorites,
        onlySourced,
      }

      try {
        const response = await sdk.Analyses(requestPayload)
        // console.log("Response Data:", response)
        return response
      } catch (err) {
        console.error("Error in useAnalyses queryFn:", err)
        throw err
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (!lastPage || lastPage.analyses?.length === 0) {
        return undefined
      }
      return lastPageParam + 1
    },
  })
}

const getAnalysisQueryKey = ({
  analysisId,
  withComps = true,
}: {
  analysisId: string
  withComps: boolean
}) => {
  return ["analysis", analysisId, withComps]
}

export const useAnalysis = (analysisId: string, withComps: boolean = true) => {
  return useQuery({
    queryKey: getAnalysisQueryKey({ analysisId, withComps }),
    queryFn: async () => {
      try {
        if (withComps) {
          return await getAnalysisWithComps(analysisId)
        }
        const response = await sdk.Analysis({ analysisId })
        /* console.log(
          "*** Getting analysis query with:",
          JSON.stringify(response, null, 2)
        ) */
        return response
      } catch (err) {
        console.error(err)
        throw err
      }
    },
    enabled: !!analysisId,
  })
}

const getAnalysisWithComps = async (analysisId: string) => {
  const response = await sdk.AnalysisComps({ analysisId })
  /* console.log(
    "*** Getting analysis + comps query with:",
    JSON.stringify(response, null, 2)
  ) */
  // Filter out null or invalid comps directly if necessary
  const compsArray = (response.analysis?.comps || []).filter(
    (comp): comp is AnalysisCompType => comp?.id != null
  )

  compsDataStore.set({ compsArray })

  return response
}

// TODO: getAnalysisWithCustomComps...

export const useAnalysisWithComps = (analysisId: string) => {
  return useQuery({
    queryKey: getAnalysisQueryKey({ analysisId, withComps: true }),
    queryFn: async () => {
      try {
        const response = await getAnalysisWithComps(analysisId)
        console.log(
          "*** Getting analysis + comps query with:",
          JSON.stringify(response, null, 2)
        )
        return response
      } catch (err) {
        console.error(err)
        throw err
      }
    },
    enabled: !!analysisId,
  })
}

export type AnalysisWithCompsType = NonNullable<
  Awaited<ReturnType<typeof getAnalysisWithComps>>["analysis"]
>

export type AnalysisNoCompsType = NonNullable<
  Awaited<ReturnType<typeof sdk.Analysis>>["analysis"]
>

export type AnalysisType = AnalysisWithCompsType | AnalysisNoCompsType

export const useEditAnalysis = () => {
  return useMutation({
    mutationFn: async (variables: EditAnalysisMutationVariables) => {
      // console.log("Sending EditAnalysis mutation with variables:", variables)
      return await sdk.EditAnalysis(variables)
    },
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(
        ["analysis", variables.analysisId, true],
        (oldData: AnalysisQuery) => {
          return {
            analysis: {
              ...oldData?.analysis,
              ...data?.updateAnalysis?.analysis,
            },
          }
        }
      )
    },
  })
}

export const useEditPropertyFeatures = () => {
  return useMutation({
    mutationFn: async (variables: EditAnalysisMutationVariables) => {
      /* console.log(
        "Sending EditPropertyFeatures mutation with variables:",
        variables
      ) */
      return await sdk.EditAnalysis(variables)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["analysis"] })
    },
  })
}

export const useAddAnalysis = () => {
  return useMutation({
    mutationFn: async (addPayload: {
      placeId: string
      bedrooms?: number
      bathrooms?: number
      area?: number
    }) => {
      const response = await sdk.AddAnalysis(addPayload)
      /* console.log(
        "*** ADDING analysis with:",
        JSON.stringify(response, null, 2)
      ) */
      return response
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["analyses"] })
    },
  })
}

export const useSetAnalysisNotification = () => {
  return useMutation({
    mutationFn: async (notificationPayload: {
      analysisId: string
      enableNotifications: boolean
    }) => {
      try {
        return await sdk.SetNotificationOnAnalysis(notificationPayload)
      } catch (err) {
        console.error(err)
      }
    },
  })
}

export const useSetFavoriteAnalysis = () => {
  return useMutation({
    mutationFn: async (favoritePayload: {
      analysisId: string
      favorite: boolean
    }) => {
      try {
        return await sdk.FavoriteAnalysis(favoritePayload)
      } catch (err) {
        console.error(err)
      }
    },
  })
}

export const useRemoveAnalysis = () => {
  return useMutation({
    mutationFn: async (removePayload: { analysisId: string }) => {
      try {
        return await sdk.RemoveAnalysis(removePayload)
      } catch (err) {
        console.error(err)
      }
    },
  })
}

export const useRunMyLeads = () => {
  return useMutation({
    mutationFn: async () => {
      return await sdk.RunMyLeads()
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["analyses"] })
    },
  })
}

export const usePropertyAutocomplete = (
  address: string | null,
  location: string | null,
  placeId: string | null = null
) => {
  return useQuery({
    queryKey: ["property-autocomplete", address, location, placeId],
    queryFn: async () => {
      return address && address !== ""
        ? await sdk.PropertyAutocomplete({ address, location, placeId })
        : null
    },
  })
}

export const useAddShareableAnalysis = () => {
  return useMutation({
    mutationFn: async (addPayload: { analysisId: string }) => {
      return await sdk.AddShareableAnalysis(addPayload)
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["analyses"] })
    },
  })
}

// Stats

export const useAnalysisStats = () => {
  return useQuery({
    queryKey: ["analysisStats"],
    queryFn: async () => {
      try {
        const response = await sdk.AnalysisData()
        const { analysisStats } = response

        if (!analysisStats) throw new Error("Failed to fetch analysis stats")

        let userType: AnalyzerUserType = "unknown"
        if (analysisStats.manualCount === 0 || !analysisStats.manualCount) {
          userType = "firstTimeUser"
        } else if (
          analysisStats.manualCount &&
          analysisStats.topAverageManualCount &&
          analysisStats.manualCount > analysisStats.topAverageManualCount * 0.9
        ) {
          userType = "topAnalyzer"
        } else if (analysisStats.manualCount > 1) {
          userType = "seasonedAnalyzer"
        }

        return {
          ...analysisStats,
          userType,
        }
      } catch (err) {
        console.error(err)
        throw new Error("Error fetching analysis stats")
      }
    },
  })
}

// Comps

export const useComp = (analysisId: string, compId: string) => {
  return useQuery({
    queryKey: ["comp", analysisId, compId],
    queryFn: async () => {
      try {
        return await sdk.GetCompDetail({ analysisId, arvCompId: compId })
      } catch (err) {
        console.error("Error fetching comp:", err)
      }
    },
  })
}

interface UpdateCompSelectionPayload {
  analysisId: string
  selectedCompIds: string[]
}

export const useUpdateCompSelectionMutation = () => {
  return useMutation({
    mutationFn: async (updatePayload: UpdateCompSelectionPayload) => {
      const { analysisId, selectedCompIds } = updatePayload
      return await sdk.UpdateCompSelections({ analysisId, selectedCompIds })
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["analyses"] })
    },
  })
}

export const useCompPreferences = (
  analysisId: string,
  defaultSettings = false
) => {
  return useQuery({
    queryKey: ["compPreferences", analysisId, defaultSettings],
    queryFn: async () => {
      try {
        const response = await sdk.GetCompQueryPreference({
          analysisId,
          default: defaultSettings,
        })
        return response
      } catch (err) {
        console.error("Error fetching comp preferences:", err)
        throw err
      }
    },
    enabled: !!analysisId,
  })
}

interface UpdateCompPreferencesPayload {
  analysisId: string
  analysisCompInput: AnalysisCompInput
}

export const useUpdateCompPreferences = () => {
  const { mutate, isSuccess, isError, data, error, isPending } = useMutation({
    mutationFn: async (updatePayload: UpdateCompPreferencesPayload) =>
      await sdk.UpdateCompQueryPreference(updatePayload),
    onSuccess: (data, variables) => {
      // Safely accessing nested properties
      const updatedAnalysis = data?.updateAnalysisComps?.analysis
      if (updatedAnalysis && variables) {
        queryClient.setQueryData(
          ["compPreferences", variables.analysisId],
          data
        )
        queryClient.setQueryData(
          ["analysis", variables.analysisId, true],
          (oldData: any) => ({
            ...oldData,
            ...updatedAnalysis,
          })
        )

        void queryClient.invalidateQueries({
          queryKey: ["analysis", variables.analysisId],
        })
      }
    },
  })

  return { mutate, isSuccess, isError, data, error, isPending }
}

export const useCreateAnalysisComp = () => {
  return useMutation<
    CreateAnalysisCompMutationMutation,
    Error,
    Exact<{ analysisId: string; address: string }>
  >({
    mutationFn: async (variables) => {
      /* console.log(
        "Sending CreateAnalysisComp mutation with variables:",
        variables
      ) */

      return await sdk.CreateAnalysisCompMutation(variables)
    },
    onSuccess: async (data, variables) => {
      const { analysisId } = variables

      // Cast `data` to a generic object to access `createAnalysisComp`
      const compData = (data as any)?.createAnalysisComp
      if (!compData) {
        console.error("Server returned null for createAnalysisComp.")
        return
      }

      if (compData.success) {
        const newComp = compData.latestManuallyAddedComp
        if (newComp) {
          addComp(newComp as AnalysisCompType)
        }

        queryClient.setQueryData<AnalysisQuery>(
          ["analysis", analysisId, true],
          (oldData) => ({
            ...(oldData || {}),
            analysis: {
              ...(oldData?.analysis || {}),
              ...compData.analysis,
            },
          })
        )
        await queryClient.invalidateQueries({ queryKey: ["analysis"] })
        await queryClient.invalidateQueries({ queryKey: ["analyses"] })
      } else if (compData.message) {
        console.error("Error in comp creation:", compData.message)
      }
    },
    onError: (error) => {
      const errorResponse = (error as any)?.response?.data
      const statusCode = errorResponse?.statusCode

      console.log(`Error Status Code: ${statusCode || "Unavailable"}`)
      if (errorResponse?.errors) {
        console.error(
          "Server returned errors:",
          JSON.stringify(errorResponse.errors)
        )
      } else {
        console.error(`*An unknown error occurred- ${JSON.stringify(error)}`)
      }
    },
  })
}
